<template>
  <Obj :id="id" :value="value">
    <Person
      v-if="value"
      ref="component"
      :value="value"
      :embedded-mode="embeddedMode"
    />
  </Obj>
</template>

<script lang="ts">
import { defineComponent, onMounted, onUpdated, toRefs, watch } from "vue";
import { useRoute } from "vue-router";

import type { Document, People } from "@ctrl/controller-rpc";

import * as inject from "@/components/inject";
import Component from "@/components/person/Person.vue";
import { bindAsync, releasableRef, useReleaser } from "@/ref";

import Obj from "./DefaultObject.vue";
import { useObject } from "./object";

export type PeopleWithTitle = People & { title: string };

export default defineComponent({
  components: {
    Obj,
    Person: Component,
  },
  props: {
    id: { type: String, required: true },
    embeddedMode: {
      type: Boolean,
      default: false,
    },
  },
  async setup(props) {
    const { id } = toRefs(props);
    const route = useRoute();
    const controller = inject.controller();
    const {
      initializeComponentFocus,
      installRouteCallbacks,
      release,
      setObjectValue,
      ...object
    } = useObject(route, (value: PeopleWithTitle) => {
      return [
        watch(
          () => value?.notes || null,
          (content: Document | null) => {
            if (content !== null) {
              controller.people.patch(
                value.id,
                {
                  notes: content,
                },
                "app"
              );
            }
          }
        ),
      ];
    });
    installRouteCallbacks();
    onMounted(initializeComponentFocus);
    onUpdated(initializeComponentFocus);
    const releaser = useReleaser();
    releaser.defer(release);
    const person = await releaser.add(
      bindAsync(id, async (id) => {
        const p = id ? await controller.people.get(id) : releasableRef(null);
        return p.map((p) =>
          p
            ? {
                ...p,
                title: p.name?.display || p.email,
                notes: p.notes || { blocks: [] },
              }
            : p
        );
      })
    );
    setObjectValue(person);
    return object;
  },
});
</script>
